import dayjs from 'dayjs'

import { getProfileTimezone } from '@/shared/composables/timezone'

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const isCompleteDateInput = (dateInput: string): boolean => (
  DATE_INPUT_PATTERN.test(dateInput)
)

export const canSyncDateInputFromModel = (dateInput: string): boolean => (
  !dateInput || isCompleteDateInput(dateInput)
)

/** Limits year segment to 4 digits while typing in native date input. */
export const sanitizeDateInputYear = (value: string): string => {
  if (!value) {
    return value
  }

  const [yearStr, ...rest] = value.split('-')
  if (!yearStr || yearStr.length <= 4) {
    return value
  }

  return [yearStr.slice(0, 4), ...rest].join('-')
}

export const isDateInputBefore = (dateInput: string, minDateInput: string): boolean => {
  if (!isCompleteDateInput(dateInput) || !isCompleteDateInput(minDateInput)) {
    return false
  }

  return dayjs.tz(dateInput).isBefore(dayjs.tz(minDateInput), 'day')
}

export const clampDateInputToMin = (dateInput: string, minDateInput: string): string => {
  if (!isCompleteDateInput(dateInput) || !isCompleteDateInput(minDateInput)) {
    return dateInput
  }

  return isDateInputBefore(dateInput, minDateInput) ? minDateInput : dateInput
}

/** For edit mode: allow saved past start date; for create mode: today only. */
export const resolveStartDateMin = (
  todayInput: string,
  savedStartTimestamp: number,
  isEdit: boolean,
): string => {
  if (!isEdit || !savedStartTimestamp) {
    return todayInput
  }

  const savedStartDate = formatTimestampToDateInput(savedStartTimestamp)
  return isDateInputBefore(savedStartDate, todayInput) ? savedStartDate : todayInput
}

/**
 * Converts a calendar day (YYYY-MM-DD string from calendar click, or legacy Date)
 * to start-of-day timestamp in the app's default timezone.
 */
export const calendarDateToStartOfDay = (date: Date | string): number => {
  const dateOnly = typeof date === 'string'
    ? date
    : dayjs(date).format('YYYY-MM-DD')

  return parseDateInputToStartOfDay(dateOnly)
}

export const formatTimestampToDateInput = (timestamp: number): string => (
  dayjs(timestamp).tz(getProfileTimezone()).format('YYYY-MM-DD')
)

/** Parses YYYY-MM-DD from date input as start of day in the default timezone. */
export const parseDateInputToStartOfDay = (dateInput: string): number => {
  if (!isCompleteDateInput(dateInput)) {
    return 0
  }

  const timezone = getProfileTimezone()
  const parsed = dayjs.tz(dateInput, timezone)
  if (!parsed.isValid()) {
    return 0
  }

  return parsed.startOf('day').valueOf()
}
